import * as React from "react";
import { useEffect, useState } from "react";

export function useOrientation() {
  const getOrientation = () => {
    if (typeof screen?.orientation !== "undefined") {
      return screen.orientation.type.startsWith("landscape")
        ? "landscape"
        : "portrait";
    }
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  };

  const [orientation, setOrientation] = useState<"portrait" | "landscape">(
    getOrientation,
  );

  useEffect(() => {
    const handler = () => setOrientation(getOrientation());

    if (screen?.orientation) {
      screen.orientation.addEventListener("change", handler);
    } else {
      window.addEventListener("resize", handler);
    }

    return () => {
      if (screen?.orientation) {
        screen.orientation.removeEventListener("change", handler);
      } else {
        window.removeEventListener("resize", handler);
      }
    };
  }, []);

  return {
    orientation,
    isPortrait: orientation === "portrait",
    isLandscape: orientation === "landscape",
  };
}

/**
 * Viewport too short to stack full-height chrome — a landscape phone (~375px),
 * NOT a landscape tablet (768px). `isLandscape` alone conflates the two, so
 * layouts that collapse "because landscape" also collapse on a 1024x768 tablet
 * that has plenty of room. Gate those on this instead.
 */
const SHORT_VIEWPORT = 500;

export function useShortViewport() {
  const [isShort, setIsShort] = React.useState(
    () => window.innerHeight < SHORT_VIEWPORT,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-height: ${SHORT_VIEWPORT - 1}px)`);
    const onChange = () => setIsShort(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isShort;
}

export function useResponsive() {
  const isMobile = useIsMobile();
  const isTouchDevice = useIsTouchDevice();
  const { isLandscape, isPortrait } = useOrientation();
  const isShortViewport = useShortViewport();
  // Landscape on a device that is actually short — a landscape tablet keeps
  // the roomy layout instead of getting the collapsed phone chrome.
  const isLandscapeMobile = isTouchDevice && isLandscape && isShortViewport;
  const isMobileOrLandscape = isMobile || isLandscapeMobile;

  return {
    isMobile,
    isTouchDevice,
    isLandscape,
    isPortrait,
    isShortViewport,
    isLandscapeMobile,
    isMobileOrLandscape,
    // side y className para sheets
    sheetSide: isLandscapeMobile ? "right" : "bottom",
    sheetClassName: isLandscapeMobile ? "rounded-l-3xl" : "rounded-t-3xl",
    showSheetClose: isLandscapeMobile,
  } as const;
}

const MOBILE_BREAKPOINT = 768;
const LG_BREAKPOINT = 1024;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

const checkTouch = () =>
  "ontouchstart" in window ||
  navigator.maxTouchPoints > 0 ||
  // @ts-expect-error - msMaxTouchPoints is for older IE/Edge
  navigator.msMaxTouchPoints > 0;

export function useIsLg() {
  const [isLg, setIsLg] = React.useState(
    () => window.innerWidth >= LG_BREAKPOINT,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);
    const onChange = () => setIsLg(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isLg;
}

export function useIsTouchDevice() {
  const [isTouchDevice, setIsTouchDevice] = React.useState(checkTouch);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsTouchDevice(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    // Fallback por si maxTouchPoints cambia tarde
    const handleTouch = () => {
      setIsTouchDevice(true);
      window.removeEventListener("touchstart", handleTouch);
    };
    window.addEventListener("touchstart", handleTouch, { passive: true });

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      window.removeEventListener("touchstart", handleTouch);
    };
  }, []);

  return isTouchDevice;
}
